import { useMemo } from 'react';
import ChartCard from '../charts/ChartCard';
import BarChart from '../charts/BarChart';
import LineChart from '../charts/LineChart';
import PolarChart from '../charts/PolarChart';
import HeatmapGrid from '../charts/HeatmapGrid';
import { fmtInt, fmtNum } from '../../utils/statsFormat';
import { dailyRows, hourlyRows, singleDayNote } from './range';
import { rangeWindowRows, windowRows } from './windowStats';
import './statistics.css';

/**
 * Section C — ADS-B.
 *
 * Gated on `adsbData` (tracks exist), NOT on the old combined `adsb` flag, so a
 * VDL2-only receiver that merely sends STATS heartbeats no longer gets a screen
 * of empty ADS-B charts.
 *
 * Two merges vs the old page: total and new aircraft per day share a chart
 * because they share a unit and the comparison is the interesting part; the
 * coverage polar and the distance histogram pair side by side.
 *
 * One split: History's activity chart put an aircraft count and a distance on
 * two y-scales in one plot. C6 and C7 are aligned panels over the same time
 * axis instead — same shape comparison, each series on its own honest scale,
 * and C7 finally reads from max_range_nm rather than the bearing outline.
 */

/** Aggregate coverage sectors into positions-per-distance-bucket bars. */
function distanceRows(heatmap) {
  const sectors = (heatmap && heatmap.sectors) || [];
  const bucket = (heatmap && heatmap.distanceSectorWidth) || 50;
  const by = new Map();
  for (const s of sectors) {
    const start = s.distanceStart || 0;
    by.set(start, (by.get(start) || 0) + (s.positionCount || 0));
  }
  return [...by.keys()].sort((a, b) => a - b).map(start => ({
    label: start === 0
      ? `<${Math.round(bucket)}`
      : `${Math.round(start)}–${Math.round(start + bucket)}`,
    values: [by.get(start)],
  }));
}

function unitLabelOf(heatmap) {
  if (!heatmap) return 'km';
  if (heatmap.units === 'imperial') return 'mi';
  if (heatmap.units === 'nautical') return 'nm';
  return 'km';
}

function AdsbSection({
  daily = [],
  hourly = [],
  coverage = null,
  reportHeatmap = null,
  reportLoading = false,
  dayAircraft = [],
  dayRangeSamples = [],
  range,
  loading = false,
}) {
  const dayNote = singleDayNote(range);

  const perDay = useMemo(
    () => dailyRows(daily, ['aircraft', 'newAircraft', 'positions', 'tracks'], range.days),
    [daily, range.days]);

  const perHour = useMemo(() => hourlyRows(hourly, ['aircraft', 'positions']), [hourly]);

  const watchRows = useMemo(() => windowRows(dayAircraft), [dayAircraft]);
  const rangeRows = useMemo(() => rangeWindowRows(dayRangeSamples), [dayRangeSamples]);

  const distRows = useMemo(() => distanceRows(coverage), [coverage]);
  const distUnit = unitLabelOf(coverage);

  const dailyEmpty = perDay.every(r => r.values.every(v => !v));

  return (
    <section className="stats-section" id="adsb">
      <header className="stats-section-header">
        <h2 className="stats-section-heading">ADS-B</h2>
        <span className="stats-section-sub">{range.label}</span>
      </header>

      <div className="stats-group-title">Per day</div>
      <div className="chart-grid">
        {/* C1 — merged: same unit, and total-vs-new is the comparison. */}
        <ChartCard title="Aircraft per day" subtitle="total vs new" loading={loading}
                   empty={dailyEmpty}
                   legend={[{ name: 'aircraft', slot: 1, mark: 'bar' },
                            { name: 'new', slot: 3, mark: 'bar' }]}>
          <BarChart mode="grouped" format={fmtInt}
                    data={perDay.map(r => ({ label: r.label, values: [r.values[0], r.values[1]] }))}
                    series={[{ name: 'aircraft', slot: 1 }, { name: 'new', slot: 3 }]} />
        </ChartCard>

        <ChartCard title="Positions per day" loading={loading} empty={dailyEmpty}>
          <BarChart format={fmtInt}
                    data={perDay.map(r => ({ label: r.label, values: [r.values[2]] }))}
                    series={[{ name: 'positions', slot: 1 }]} />
        </ChartCard>

        <ChartCard title="Tracks per day" loading={loading} empty={dailyEmpty}>
          <BarChart format={fmtInt}
                    data={perDay.map(r => ({ label: r.label, values: [r.values[3]] }))}
                    series={[{ name: 'tracks', slot: 1 }]} />
        </ChartCard>
      </div>

      <div className="stats-group-title">Per hour · UTC</div>
      <div className="chart-grid">
        {/* C4/C5 kept apart: aircraft counts and position counts differ by
            orders of magnitude, so one scale would flatten the smaller. */}
        <ChartCard title="Aircraft per hour" note={dayNote} loading={loading}>
          <BarChart format={fmtInt} maxLabels={12}
                    data={perHour.map(r => ({ label: r.label, values: [r.values[0]] }))}
                    series={[{ name: 'aircraft', slot: 2 }]} />
        </ChartCard>

        <ChartCard title="Positions per hour" note={dayNote} loading={loading}>
          <BarChart format={fmtInt} maxLabels={12}
                    data={perHour.map(r => ({ label: r.label, values: [r.values[1]] }))}
                    series={[{ name: 'positions', slot: 2 }]} />
        </ChartCard>
      </div>

      <div className="stats-group-title">Activity · 10-minute windows</div>
      <div className="chart-grid">
        {/* C6 + C7: aligned panels, one time axis, two honest scales. */}
        <ChartCard title="Aircraft on watch" subtitle="concurrent, low → peak"
                   note={dayNote} span="full" loading={loading}
                   empty={watchRows.length === 0}
                   emptyText="No tracked aircraft for this day"
                   legend={[{ name: 'held through window', slot: 2, mark: 'bar' },
                            { name: 'peak above floor', slot: 1, mark: 'bar' }]}>
          <BarChart mode="stacked" format={fmtInt} maxLabels={12} height={220}
                    data={watchRows}
                    series={[{ name: 'held through window', slot: 2 },
                             { name: 'peak above floor', slot: 1 }]} />
        </ChartCard>

        <ChartCard title="Furthest contact" subtitle="nm · per 10-minute window"
                   note={dayNote} span="full" loading={loading}
                   empty={rangeRows.length === 0}
                   emptyText="No live range data for this day (receiver sends no STATS)">
          <LineChart unit="nm" height={200} format={(x) => fmtNum(x, 0)}
                     series={[{ name: 'max range', slot: 1, samples: rangeRows }]} />
        </ChartCard>
      </div>

      <div className="stats-group-title">Coverage</div>
      <div className="chart-grid">
        <ChartCard title="Coverage by bearing & distance" note={dayNote} span="2"
                   loading={loading}
                   empty={!coverage || !(coverage.sectors || []).some(s => s.positionCount > 0)}>
          <PolarChart heatmap={coverage} unitLabel={distUnit} />
        </ChartCard>

        <ChartCard title={`Positions by distance (${distUnit})`} note={dayNote} span="2"
                   loading={loading} empty={distRows.length === 0}>
          <BarChart format={fmtInt} data={distRows}
                    series={[{ name: 'positions', slot: 1 }]} />
        </ChartCard>
      </div>

      <div className="stats-group-title">Report activity</div>
      <div className="chart-grid">
        <ChartCard title="Hourly received reports" subtitle="UTC" span="full"
                   note={range.heatmapNote} loading={reportLoading}
                   empty={!reportHeatmap || (reportHeatmap.cells || []).length === 0}>
          <HeatmapGrid cells={reportHeatmap?.cells || []}
                       maxValue={reportHeatmap?.maxPositions || 0} />
        </ChartCard>
      </div>
    </section>
  );
}

export default AdsbSection;
